import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { IJwtPayload, IUserInfo } from '../interfaces/user';
import { jwtDecode } from 'jwt-decode';
import { getuserInfo } from '../utils/tokenUtils';
import { IUserLoginRes } from '@/interfaces/login';
import { EUserRole } from '@/enums/EUserRole';

interface AuthContextType {
  isAuthenticated: boolean;
  userInfo: IUserInfo | null;
  login: (loginRes: IUserLoginRes) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const currentToken = Cookies.get('access_token') || '';
  const [isAuthenticated, setIsAuthenticated] =
    useState<boolean>(!!currentToken);
  const [userInfo, setUserInfo] = useState<IUserInfo | null>(
    getuserInfo(currentToken),
  );

  useEffect(() => {
    const token = Cookies.get('access_token');
    if (token) {
      setIsAuthenticated(true);
      const decodedToken = jwtDecode<IJwtPayload>(token);
      setUserInfo({
        id: decodedToken.nameid,
        name: decodedToken.unique_name,
        email: decodedToken.email,
        role: (decodedToken.role as EUserRole) ?? EUserRole.Employee,
        permissions:
          typeof decodedToken.permissions === 'string'
            ? [decodedToken.permissions]
            : decodedToken.permissions,
        bu: Number(decodedToken.bu) || null,
        buName: decodedToken.buName,
      });
    }
  }, []);

  const login = (loginRes: IUserLoginRes) => {
    setIsAuthenticated(true);
    setUserInfo(getuserInfo(loginRes.accessToken));
    Cookies.set('access_token', loginRes.accessToken, {
      expires: 7,
      secure: false,
    });
  };

  const logout = () => {
    Cookies.remove('access_token');
    setIsAuthenticated(false);
    setUserInfo(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, userInfo, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
